import React, { useState } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import SafeMarkdown from '../../shared/SafeMarkdown';
import { assistantChatMarkdownSx } from '../../shared/markdownBodySx';
import { colors } from '../../../designTokens';
import ContextAddMenu from '../../shared/context-display/ContextAddMenu';
import KnowledgeLibraryResultCard from './KnowledgeLibraryResultCard';
import { filterVisibleKnowledgeResults } from '../../freestyle/discovery/discoveryRelevanceStyles';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../../freestyle/assistantButtonStyles';

export default function KnowledgeReviewCard({
  suggestion = '',
  recommendations = [],
  analyzingGaps = false,
  searchingLibrary = false,
  results = [],
  discoveryError = '',
  showReevaluate = false,
  onAddKnowledge,
  onAttachExisting,
  onReevaluate,
  onDone,
  doneLabel = 'Done',
  nextStep = 'finish',
  onAddResult,
  onOpenResult,
  showActions = true,
  compact = false,
}) {
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const showAddKnowledge = typeof onAddKnowledge === 'function' || typeof onAttachExisting === 'function';
  const displayedResults = filterVisibleKnowledgeResults(results);
  const nextStepCopy = nextStep === 'run'
    ? {
      title: 'Ready to run this capability?',
      description: 'Add the recommended knowledge to make the result more specific, or run the capability with what you have.',
    }
    : nextStep === 'done'
      ? {
        title: 'Ready to continue?',
        description: `Add any helpful knowledge, then select ${doneLabel} when you are ready to continue.`,
      }
      : {
        title: 'Ready to finish this capability?',
        description: `Add helpful knowledge, then select ${doneLabel} to generate the details and variables that make it ready to reuse.`,
      };
  const actionableRecommendations = Array.isArray(recommendations)
    ? recommendations.filter((recommendation) => String(recommendation?.item || '').trim())
    : [];
  const runtimeInputs = actionableRecommendations.filter((item) => /^\s*\{\{[^}]+\}\}\s*$/.test(item.item));
  const knowledgeRecommendations = actionableRecommendations.filter((item) => !runtimeInputs.includes(item));

  return (
    <Stack spacing={compact ? 1 : 1.5} sx={{ mt: compact ? 0 : 1.5 }}>
      <Box>
        <Typography component="h3" sx={{ color: colors.textPrimary, fontSize: '0.875rem', fontWeight: 600, letterSpacing: 0 }}>
          {knowledgeRecommendations.length || !runtimeInputs.length ? 'Recommended knowledge' : 'Run inputs'}
        </Typography>
        {analyzingGaps ? (
          <Typography variant="body2" sx={{ mt: 0.75, color: colors.textSecondary }}>
            Analyzing knowledge gaps…
          </Typography>
        ) : (
          actionableRecommendations.length > 0 ? (
            <Stack component="ol" spacing={0.5} sx={{ mt: 0.75, mb: 0, pl: 2.5 }}>
              {(knowledgeRecommendations.length ? knowledgeRecommendations : runtimeInputs).map((recommendation, index) => {
                const required = recommendation.priority === 'required';
                return (
                  <Typography
                    component="li"
                    key={`${recommendation.item}-${index}`}
                    sx={{ color: colors.textPrimary, fontSize: '0.875rem', lineHeight: 1.45 }}
                  >
                    <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 750 }}>
                      {required ? 'Required: ' : 'Recommended: '}
                    </Box>
                    {recommendation.item}
                  </Typography>
                );
              })}
            </Stack>
          ) : suggestion ? (
            <Box sx={{ mt: 0.75, '& > *:first-of-type': { mt: 0 } }}>
              <SafeMarkdown markdownBaseSx={assistantChatMarkdownSx}>{suggestion}</SafeMarkdown>
            </Box>
          ) : null
        )}
        {runtimeInputs.length > 0 && knowledgeRecommendations.length > 0 && <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>Run inputs</Typography>
          <Typography variant="body2" color="text.secondary">{runtimeInputs.map((item) => item.item).join(', ')}</Typography>
        </Box>}
      </Box>

      <Divider />

      <Box>
        <Typography component="h3" sx={{ color: colors.textPrimary, fontSize: '0.875rem', fontWeight: 600, letterSpacing: 0 }}>
          Relevant knowledge
        </Typography>
        {analyzingGaps ? (
          <Typography variant="body2" sx={{ mt: 0.75, color: colors.textSecondary }}>
            Preparing relevant knowledge recommendations…
          </Typography>
        ) : searchingLibrary ? (
          <Typography variant="body2" sx={{ mt: 0.75, color: colors.textSecondary }}>
            Searching your knowledge library…
          </Typography>
        ) : discoveryError ? (
          <Typography variant="body2" color="warning.main" sx={{ mt: 0.75, lineHeight: 1.45 }}>
            {discoveryError}. You can still attach knowledge manually.
          </Typography>
        ) : displayedResults.length === 0 ? (
          <Typography variant="body2" sx={{ mt: 0.75, color: colors.textSecondary }}>
            No strong saved knowledge matches. You can still choose knowledge from your library.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {displayedResults.map((result) => {
              const item = result.context || result.raw || result;
              return (
                <KnowledgeLibraryResultCard
                  key={result.id || item.id}
                  result={result}
                  onPreview={(ctx, res) => onOpenResult?.(ctx, res)}
                  onAdd={onAddResult}
                />
              );
            })}
          </Stack>
        )}
      </Box>

      {showActions && !analyzingGaps && !searchingLibrary && (
        <>
          <Divider />
          <Box
            sx={{
              py: 1,
            }}
          >
            <Typography sx={{ color: colors.textPrimary, fontSize: '0.8125rem', fontWeight: 750 }}>
              {nextStepCopy.title}
            </Typography>
            <Typography sx={{ mt: 0.25, color: colors.textSecondary, fontSize: '0.8125rem', lineHeight: 1.45 }}>
              {nextStepCopy.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {showAddKnowledge && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                  sx={assistantSecondaryButtonSx}
                >
                  Add knowledge
                </Button>
                <ContextAddMenu
                  anchorEl={addMenuAnchor}
                  open={Boolean(addMenuAnchor)}
                  onClose={() => setAddMenuAnchor(null)}
                  onCreateTemporary={onAddKnowledge}
                  onAttachExisting={onAttachExisting}
                />
              </>
            )}
            {showReevaluate && onReevaluate && (
              <Button size="small" onClick={onReevaluate} sx={assistantTextButtonSx}>
                Re-evaluate
              </Button>
            )}
            {onDone && (
              <Button size="small" variant="contained" onClick={onDone} sx={assistantPrimaryButtonSx}>
                {doneLabel}
              </Button>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
